import React, { useEffect, useRef, useState } from 'react';
import { Button, Checkbox, Select, Tag, Tooltip } from 'antd';
import {
  StaffCaseSearchContainerCaseSearchResultFieldsFragment,
  StaffCaseSearchFilterContainerEntityFieldsFragment,
  StaffCaseSearchFilterContainerStaffUsersFieldsFragment
} from '../../../../generated';
import { useSearchParams } from 'react-router-dom';
import FilterOptionLabel from '../../../shared/filter-option-label';
import { ReloadOutlined } from '@ant-design/icons';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
const { Option } = Select;

const transformCount = (input: any) => {
  if (!input) {
    return {};
  }
  return Object.keys(input)
    .filter((key) => key !== '__typename')
    .reduce((acc: any, key) => {
      const values = input[key]?.reduce((subAcc: any, detail: any) => {
        subAcc[detail.value] = detail.count;
        return subAcc;
      }, {});
      acc[key] = values;
      return acc;
    }, {});
};

const updatesFiltersOptions = [
  { idSuffix: 'today', label: 'Today' },
  { idSuffix: 'yesterday', label: 'Yesterday' },
  { idSuffix: 'lastWeek', label: 'Within 7 days' },
  { idSuffix: 'lastMonth', label: 'Within 30 days' },
  { idSuffix: 'lastYear', label: 'Within 365 days' }
];

const updatesFilters = [
  { label: 'Last Updated', category: 'updatedAt', items: updatesFiltersOptions },
  { label: 'Created At', category: 'createdAt', items: updatesFiltersOptions },
  { label: 'Decision Completed At', category: 'decisionCompletedAt', items: updatesFiltersOptions },
  { label: 'Expiration Eligible At', category: 'expirationEligibleAt', items: updatesFiltersOptions },
  { label: 'LastActivity Created At', category: 'lastActivityCreatedAt', items: updatesFiltersOptions },
  { label: 'Revision Request Requested At', category: 'revisionRequestRequestedAt', items: updatesFiltersOptions },
  { label: 'Revision Request Submitted At', category: 'revisionRequestSubmittedAt', items: updatesFiltersOptions },
  { label: 'Submitted At', category: 'submittedAt', items: updatesFiltersOptions }
];

const caseTypeFilters = [
  { value: 'id-verification', label: 'Identity Verification' },
  { value: 'credential-verification', label: 'Credential Verification' },
  { value: 'send-report', label: 'Send Report' }
];

const credentialTypeFilter = [
  { value: 'primary', label: 'Primary' },
  { value: 'other', label: 'Other' }
];

const stateFilters = [
  { value: 'draft', label: 'Draft' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'caseWorkerAssigned', label: 'Assigned' },
  { value: 'revisionRequested', label: 'Revision Requested' },
  { value: 'revisionSubmitted', label: 'Revision Submitted' },
  { value: 'decisionRendered', label: 'Decision Rendered' }
];

const decisionResultFilter = [
  { value: 'completed', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'pending', label: 'No Decision' }
];

const schemaVersionFilter = [{ value: '1.0.0', label: '1.0.0' }];

export interface CaseCheckFilterInterface {
  state: string[];
  caseWorkerAssigned: string[];
  decisionResult: string[];
  caseType: string[];
  updatedAt: string[];
  createdAt: string[];
  decisionCompletedAt: string[];
  expirationEligibleAt: string[];
  lastActivityCreatedAt: string[];
  revisionRequestRequestedAt: string[];
  revisionRequestSubmittedAt: string[];
  submittedAt: string[];
  decisionCompletedBy: string[];
  revisionRequestRequestedBy: string[];
  clientId: string[];
  credentialType: string[];
  schemaVersion: string[];
  tags: string[];
}

interface CaseSearchFilterProps {
  data: StaffCaseSearchContainerCaseSearchResultFieldsFragment;
  staffUserData: StaffCaseSearchFilterContainerStaffUsersFieldsFragment;
  staffUserList: StaffCaseSearchFilterContainerStaffUsersFieldsFragment[];
  entitiesList: StaffCaseSearchFilterContainerEntityFieldsFragment[];
  allTags: string[];
  refreshTagHandler: () => void;
}

export const CaseSearchFilter: React.FC<CaseSearchFilterProps> = (props) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const count = transformCount(props?.data);
  const [checkedFilter, setCheckedFilter] = useState<CaseCheckFilterInterface>({
    state: searchParams.get('state')?.split(',') || [],
    caseWorkerAssigned: searchParams.get('caseWorkerAssigned')?.split(',') || [],
    decisionResult: searchParams.get('decisionResult')?.split(',') || [],
    caseType: searchParams.get('caseType')?.split(',') || [],
    updatedAt: searchParams.get('updatedAt')?.split(',') || [],
    createdAt: searchParams.get('createdAt')?.split(',') || [],
    decisionCompletedAt: searchParams.get('decisionCompletedAt')?.split(',') || [],
    expirationEligibleAt: searchParams.get('expirationEligibleAt')?.split(',') || [],
    lastActivityCreatedAt: searchParams.get('lastActivityCreatedAt')?.split(',') || [],
    revisionRequestRequestedAt: searchParams.get('revisionRequestRequestedAt')?.split(',') || [],
    revisionRequestSubmittedAt: searchParams.get('revisionRequestSubmittedAt')?.split(',') || [],
    submittedAt: searchParams.get('submittedAt')?.split(',') || [],
    decisionCompletedBy: searchParams.get('decisionCompletedBy')?.split(',') || [],
    revisionRequestRequestedBy: searchParams.get('revisionRequestRequestedBy')?.split(',') || [],
    clientId: searchParams.get('clientId')?.split(',') || [],
    credentialType: searchParams.get('credentialType')?.split(',') || [],
    schemaVersion: searchParams.get('schemaVersion')?.split(',') || [],
    tags: searchParams.get('tags')?.split(',') || []
  });

  const hasInitialTagsSet = useRef(false);

  const handleFilterCheck = (event: any, filterCategory: keyof typeof checkedFilter) => {
    searchParams.set('page', '1');
    const { id: filterId, checked } = event.target;

    if (!checked) {
      const currentSearchCategory = searchParams.get(filterCategory);
      if (currentSearchCategory) {
        const updatedSearchCategory = currentSearchCategory.split(',').filter((item) => item !== filterId);
        if (updatedSearchCategory.length > 0) {
          searchParams.set(filterCategory, updatedSearchCategory.join(','));
        } else {
          searchParams.delete(filterCategory);
        }
        setSearchParams(searchParams);
      }
    } else {
      const currentSearchCategory = searchParams.get(filterCategory);
      if (currentSearchCategory) {
        const updatedSearchCategory = currentSearchCategory.split(',');
        updatedSearchCategory.push(filterId);
        searchParams.set(filterCategory, updatedSearchCategory.join(','));
        setSearchParams(searchParams);
      } else {
        searchParams.set(filterCategory, filterId);
        setSearchParams(searchParams);
      }
    }

    const filterName = filterId;
    setCheckedFilter((prevState) => {
      const currentCategory = prevState[filterCategory];

      if (Array.isArray(currentCategory)) {
        const updatedCategory = checked ? [...currentCategory, filterName] : currentCategory.filter((item: any) => item !== filterName);

        return {
          ...prevState,
          [filterCategory]: updatedCategory
        };
      }
      return prevState;
    });
  };

  const isFilterSelected = (idSuffix: string, category: string) => {
    return searchParams.get(category)?.split(',').includes(idSuffix);
  };

  useEffect(() => {
    if (props.data && count.tags && !hasInitialTagsSet.current) {
      hasInitialTagsSet.current = true;
    }
  }, [count.tags, props.data]);

  const handleSelectChange = (paramName: keyof typeof checkedFilter, value: string[]) => {
    searchParams.set('page', '1');
    const paramValue = value.join(',');
    paramValue ? searchParams.set(paramName, paramValue) : searchParams.delete(paramName);
    setSearchParams(searchParams);
    setCheckedFilter((prevState) => ({
      ...prevState,
      [paramName]: value
    }));
  };

  const handleCaseWorkerAssigned = (event: any, id: string) => {
    // reset page to 1 when filter is applied
    searchParams.set('page', '1');
    const { checked } = event.target;
    const currentCaseWorkersAssigned = searchParams.get('caseWorkerAssigned')?.split(',') || [];
    if (checked) {
      if (!currentCaseWorkersAssigned.includes(id)) {
        // add case worker id to the list
        currentCaseWorkersAssigned.push(id);
      }
    } else {
      const index = currentCaseWorkersAssigned.indexOf(id);
      if (index > -1) {
        // remove case worker id from the list
        currentCaseWorkersAssigned.splice(index, 1);
      }
    }

    // update search params
    if (currentCaseWorkersAssigned.length > 0) {
      // add case worker id to the search params
      searchParams.set('caseWorkerAssigned', currentCaseWorkersAssigned.join(','));
    } else {
      // remove caseWorkerAssigned from search params if no case worker is selected
      searchParams.delete('caseWorkerAssigned');
    }
    setSearchParams(searchParams);

    setCheckedFilter((prevState) => ({
      ...prevState,
      caseWorkerAssigned: currentCaseWorkersAssigned
    }));
  };

  const clearAllFilter = () => {
    const filterKeys: (keyof CaseCheckFilterInterface)[] = [
      'state',
      'caseWorkerAssigned',
      'decisionResult',
      'caseType',
      'updatedAt',
      'createdAt',
      'decisionCompletedAt',
      'expirationEligibleAt',
      'lastActivityCreatedAt',
      'revisionRequestRequestedAt',
      'revisionRequestSubmittedAt',
      'submittedAt',
      'decisionCompletedBy',
      'revisionRequestRequestedBy',
      'clientId',
      'credentialType',
      'schemaVersion',
      'tags'
    ];

    const hasFilters = filterKeys.some((key) => searchParams.get(key));

    if (!hasFilters) {
      return;
    }

    filterKeys.forEach((key) => {
      searchParams.delete(key);
    });

    setSearchParams(searchParams);

    setCheckedFilter({
      state: [],
      caseWorkerAssigned: [],
      decisionResult: [],
      caseType: [],
      updatedAt: [],
      createdAt: [],
      decisionCompletedAt: [],
      expirationEligibleAt: [],
      lastActivityCreatedAt: [],
      revisionRequestRequestedAt: [],
      revisionRequestSubmittedAt: [],
      submittedAt: [],
      decisionCompletedBy: [],
      revisionRequestRequestedBy: [],
      clientId: [],
      credentialType: [],
      schemaVersion: [],
      tags: []
    });
  };

  const openFilter = (filterCategory: keyof typeof checkedFilter) => {
    return checkedFilter[filterCategory].length > 0;
  };

  const permissions = props?.staffUserData?.role?.permissions;
  const idCasePermission = permissions?.idCasePermissions;
  const credCasePermission = permissions?.credCasePermissions;
  const sendCasePermission = permissions?.sendCasePermissions;

  return (
    <div className="flex-[1_1_30%] mr-[10px] min-w-[220px] max-w-[300px] max-h-[200vh] overflow-x-hidden">
      <div style={{ marginBottom: '15px' }}>
        <Button onClick={clearAllFilter} type="primary">
          Clear All Filters
        </Button>
      </div>

      <div className="overflow-x-hidden overflow-y-auto max-h-[750px] scrollbar-hide">
        <div className="flex flex-wrap w-[200px] mb-2">
          {checkedFilter?.tags.map((tag) => (
            <Tag
              className="mb-2"
              key={tag}
              closable
              onClose={() => {
                handleFilterCheck({ target: { checked: false, id: tag } }, 'tags');
              }}
            >
              {tag}
            </Tag>
          ))}
        </div>
        {idCasePermission?.canSearchCases || credCasePermission?.canSearchCases || sendCasePermission?.canSearchCases ? (
          <FilterOptionLabel label="Case Type" initiallyOpen={openFilter('caseType')}>
            {caseTypeFilters.map(
              (option) =>
                (option.value !== 'id-verification' || idCasePermission?.canSearchCases) &&
                (option.value !== 'credential-verification' || credCasePermission?.canSearchCases) &&
                (option.value !== 'send-report' || sendCasePermission?.canSearchCases) && (
                  <Checkbox
                    key={option.value}
                    id={option.value}
                    checked={isFilterSelected(option.value, 'caseType')}
                    onChange={(event: CheckboxChangeEvent) => {
                      handleFilterCheck(event, 'caseType' as keyof typeof checkedFilter);
                    }}
                  >
                    <p className="checkBoxLabel">
                      {option.label} ({count['caseType']?.[option.value] ?? 0})
                    </p>
                  </Checkbox>
                )
            )}
          </FilterOptionLabel>
        ) : null}

        <FilterOptionLabel label="State" initiallyOpen={openFilter('state')}>
          {stateFilters.map((option) => (
            <Checkbox
              key={option.value}
              id={option.value}
              checked={isFilterSelected(option.value, 'state')}
              onChange={(event: CheckboxChangeEvent) => {
                handleFilterCheck(event, 'state' as keyof typeof checkedFilter);
              }}
            >
              <p className="checkBoxLabel">
                {option.label} ({count['state']?.[option.value] ?? 0})
              </p>
            </Checkbox>
          ))}
        </FilterOptionLabel>

        {(idCasePermission?.canViewUnassignedCases ||
          idCasePermission?.canViewCasesAssignedToOthers ||
          credCasePermission?.canViewUnassignedCases ||
          credCasePermission?.canViewCasesAssignedToOthers ||
          sendCasePermission?.canViewUnassignedCases ||
          sendCasePermission?.canViewCasesAssignedToOthers) && (
          <FilterOptionLabel label="Assignment" initiallyOpen={openFilter('caseWorkerAssigned')}>
            <Checkbox
              key={'assignedToMe'}
              id={'assignedToMe'}
              checked={isFilterSelected(props.staffUserData?.id, 'caseWorkerAssigned')}
              onChange={(e: any) => {
                handleCaseWorkerAssigned(e, props.staffUserData?.id);
              }}
            >
              <p className="checkBoxLabel">Assigned to Me ({count['caseWorkerAssigned']?.[props.staffUserData?.id] ?? 0})</p>
            </Checkbox>
            {idCasePermission?.canViewUnassignedCases || credCasePermission?.canViewUnassignedCases || sendCasePermission?.canViewUnassignedCases ? (
              <Checkbox
                key={'unassigned'}
                id={'unassigned'}
                checked={isFilterSelected('unassigned', 'caseWorkerAssigned')}
                onChange={(e: any) => {
                  handleCaseWorkerAssigned(e, 'unassigned');
                }}
              >
                <p className="checkBoxLabel">Unassigned ({count['caseWorkerAssigned']?.['unassigned'] ?? 0})</p>
              </Checkbox>
            ) : null}

            {idCasePermission?.canViewCasesAssignedToOthers || credCasePermission?.canViewCasesAssignedToOthers || sendCasePermission?.canViewCasesAssignedToOthers ? (
              <Select
                mode="multiple"
                autoClearSearchValue
                showSearch={true}
                optionFilterProp="label"
                allowClear
                className="w-52 my-2"
                placeholder="Select Case Worker"
                value={checkedFilter.caseWorkerAssigned.filter((id) => id !== 'unassigned' && id !== props.staffUserData?.id)}
                onClear={() => {
                  setCheckedFilter((prevState) => ({
                    ...prevState,
                    caseWorkerAssigned: []
                  }));
                  searchParams.delete('caseWorkerAssigned');
                  setSearchParams(searchParams);
                }}
                onSelect={(value: any) => {
                  handleCaseWorkerAssigned({ target: { checked: true } }, value);
                }}
                onDeselect={(value: any) => {
                  handleCaseWorkerAssigned({ target: { checked: false } }, value);
                }}
              >
                {props.staffUserList.map((staffUser: any) => (
                  <Option key={staffUser.id} value={staffUser.id}>
                    <Tooltip placement="right" title={`${staffUser.displayName}(${count['caseWorkerAssigned']?.[staffUser.id] ?? 0})`}>
                      {staffUser.displayName} ({count['caseWorkerAssigned']?.[staffUser.id] ?? 0})
                    </Tooltip>
                  </Option>
                ))}
              </Select>
            ) : null}
          </FilterOptionLabel>
        )}

        <FilterOptionLabel label="Revision Requested By" initiallyOpen={openFilter('revisionRequestRequestedBy')}>
          <Select
            mode="multiple"
            style={{ width: '200px' }}
            placeholder="Select Revision Requested By"
            allowClear
            value={checkedFilter.revisionRequestRequestedBy}
            onChange={(value) => handleSelectChange('revisionRequestRequestedBy', value)}
          >
            {props.staffUserList.map((staffUser: any) => (
              <Option key={staffUser.id} value={staffUser.id}>
                <Tooltip placement="right" title={staffUser.displayName}>
                  {staffUser.displayName}
                </Tooltip>
              </Option>
            ))}
          </Select>
        </FilterOptionLabel>

        <FilterOptionLabel label="Client" initiallyOpen={openFilter('clientId')}>
          <Select
            mode="multiple"
            allowClear
            style={{ width: '200px' }}
            placeholder="Select Client"
            value={checkedFilter.clientId}
            onChange={(value) => handleSelectChange('clientId', value)}
          >
            {props.entitiesList.filter((entity: StaffCaseSearchFilterContainerEntityFieldsFragment) => entity.isClient === true).map((entity: StaffCaseSearchFilterContainerEntityFieldsFragment) => (
              <Option key={entity.id} value={entity.id}>
                <Tooltip placement="right" title={`${entity.entityName} (${count['clientId']?.[entity.id] ?? 0})`}>
                  {entity.entityName}({count['clientId']?.[entity.id] ?? 0})
                </Tooltip>
              </Option>
            ))}
          </Select>
        </FilterOptionLabel>

        <FilterOptionLabel label="Credential Type" initiallyOpen={openFilter('credentialType')}>
          {credentialTypeFilter.map((option) => (
            <Checkbox
              key={option.value}
              id={option.value}
              checked={isFilterSelected(option.value, 'credentialType')}
              onChange={(event: CheckboxChangeEvent) => {
                handleFilterCheck(event, 'credentialType' as keyof typeof checkedFilter);
              }}
            >
              <p className="checkBoxLabel">
                {option.label} ({count['credentialType']?.[option.value] ?? 0})
              </p>
            </Checkbox>
          ))}
        </FilterOptionLabel>

        <FilterOptionLabel label="Decision" initiallyOpen={openFilter('decisionResult')}>
          {decisionResultFilter.map((option) => (
            <Checkbox
              key={option.value}
              id={option.value}
              checked={isFilterSelected(option.value, 'decisionResult')}
              onChange={(event: CheckboxChangeEvent) => {
                handleFilterCheck(event, 'decisionResult' as keyof typeof checkedFilter);
              }}
            >
              <p className="checkBoxLabel">
                {option.label} ({count['decisionResult']?.[option.value] ?? 0})
              </p>
            </Checkbox>
          ))}
        </FilterOptionLabel>

        <FilterOptionLabel label="Decision Completed By" initiallyOpen={openFilter('decisionCompletedBy')}>
          <Select
            mode="multiple"
            style={{ width: '200px' }}
            allowClear
            placeholder="Select Decision Completed By"
            value={checkedFilter.decisionCompletedBy}
            onChange={(value) => handleSelectChange('decisionCompletedBy', value)}
          >
            {props.staffUserList.map((staffUser: any) => (
              <Option key={staffUser.id} value={staffUser.id}>
                <Tooltip placement="right" title={`${staffUser.displayName} (${count['decisionCompletedBy']?.[staffUser.id] ?? 0})`}>
                  {staffUser.displayName}({count['decisionCompletedBy']?.[staffUser.id] ?? 0})
                </Tooltip>
              </Option>
            ))}
          </Select>
        </FilterOptionLabel>

        <FilterOptionLabel label="Tag" initiallyOpen={openFilter('tags')} icon={<ReloadOutlined />} iconHandler={props?.refreshTagHandler} iconToolTipText="Refresh Tags">
          <Select
            mode="multiple"
            style={{ width: '200px' }}
            allowClear
            placeholder="Select Tag"
            value={checkedFilter.tags}
            onChange={(value) => handleSelectChange('tags', value)}
          >
            {props?.allTags?.map((tag) => (
              <Option key={tag} value={tag}>
                <Tooltip placement="right" title={`${tag} (${count['tags']?.[tag] ?? 0})`}>
                  {tag}({count['tags']?.[tag] ?? 0})
                </Tooltip>
              </Option>
            ))}
          </Select>
        </FilterOptionLabel>

        {updatesFilters.map((filter) => (
          <FilterOptionLabel key={filter.category} label={filter.label} initiallyOpen={openFilter(filter.category as keyof CaseCheckFilterInterface)}>
            {updatesFiltersOptions.map((option) => (
              <Checkbox
                key={option.idSuffix}
                id={option.idSuffix}
                checked={isFilterSelected(option.idSuffix, filter.category)}
                onChange={(event: CheckboxChangeEvent) => {
                  handleFilterCheck(event, filter.category as keyof typeof checkedFilter);
                }}
              >
                <p className="checkBoxLabel">
                  {option.label} ({count[filter.category]?.[option.idSuffix] ?? 0})
                </p>
              </Checkbox>
            ))}
          </FilterOptionLabel>
        ))}

        <FilterOptionLabel label="Schema Version" initiallyOpen={openFilter('schemaVersion')}>
          {schemaVersionFilter.map((option) => (
            <Checkbox
              key={option.value}
              id={option.value}
              checked={isFilterSelected(option.value, 'schemaVersion')}
              onChange={(event: CheckboxChangeEvent) => {
                handleFilterCheck(event, 'schemaVersion' as keyof typeof checkedFilter);
              }}
            >
              <p className="checkBoxLabel">
                {option.label} ({count['schemaVersion']?.[option.value] ?? 0})
              </p>
            </Checkbox>
          ))}
        </FilterOptionLabel>
      </div>
    </div>
  );
};
